import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { IconSelector } from './IconSelector';
import LogoUploader from './LogoUploader';
import { KnowledgeBaseIcon } from '../../schemas/knowledgeBase';
import { KnowledgeBase as KnowledgeBaseType } from '../../services/knowledgeBaseService';
import { createIconData, createCoverData, KnowledgeBaseIconType } from '../../types/knowledgeBase';
import { knowledgeBaseService } from '../../services/knowledgeBaseService';
import clsx from 'clsx';

interface CreateKnowledgeBaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (knowledgeBase: KnowledgeBaseType) => void;
  knowledgeBase?: KnowledgeBaseType | null;
}

export const CreateKnowledgeBaseDialog: React.FC<CreateKnowledgeBaseDialogProps> = ({
  open,
  onClose,
  onSuccess,
  knowledgeBase,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!knowledgeBase;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconType, setIconType] = useState<KnowledgeBaseIconType>({
    type: 'cover',
    cover_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useCustomCover, setUseCustomCover] = useState(true);

  // Initialize form with knowledge base data when editing
  useEffect(() => {
    if (knowledgeBase) {
      setName(knowledgeBase.name || '');
      setDescription(knowledgeBase.description || '');
      // Initialize icon type from metadata
      if (knowledgeBase.meta_data?.icon_type) {
        setIconType(knowledgeBase.meta_data.icon_type);
        setUseCustomCover(knowledgeBase.meta_data.icon_type.type === 'cover');
      } else {
        // Default to cover mode with existing cover
        const coverUrl = knowledgeBase.meta_data?.cover_url || '';
        setIconType({ type: 'cover', cover_url: coverUrl });
        setUseCustomCover(true);
      }
    } else {
      // Reset form for create mode
      setName('');
      setDescription('');
      setIconType({ type: 'cover', cover_url: '' });
      setUseCustomCover(true);
    }
    setError(null);
  }, [knowledgeBase, open]);

  // Reset form when dialog opens (for create mode)
  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setIconType({ type: 'cover', cover_url: '' });
    setUseCustomCover(true);
    setError(null);
    setLoading(false);
  }, []);

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!name.trim()) {
      setError(t('knowledge.dialog.nameRequired'));
      return;
    }

    // Validate that cover is uploaded if in cover mode (only for create mode)
    if (!isEditMode && useCustomCover && iconType.type === 'cover' && !iconType.cover_url) {
      setError(t('knowledge.dialog.coverRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result: KnowledgeBaseType | null;

      if (isEditMode && knowledgeBase) {
        // Update existing knowledge base
        result = await knowledgeBaseService.updateKnowledgeBase(knowledgeBase.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          iconType,
        });
        if (!result) {
          setError(t('knowledge.dialog.updateFailed'));
        }
      } else {
        // Create new knowledge base
        result = await knowledgeBaseService.createKnowledgeBase({
          name: name.trim(),
          description: description.trim() || undefined,
          iconType,
        });
        if (!result) {
          setError(t('knowledge.dialog.createFailed'));
        }
      }

      if (result) {
        onSuccess?.(result as KnowledgeBaseType);
        resetForm();
        onClose();
      }
    } catch (err) {
      console.error('Failed to save knowledge base:', err);
      setError(
        isEditMode ? t('knowledge.dialog.updateFailed') : t('knowledge.dialog.createFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  // Clear error when user starts typing
  const handleNameChange = (value: string) => {
    setName(value);
    if (error) {
      setError(null);
    }
  };

  // Handle icon selection
  const handleIconChange = (icon: KnowledgeBaseIcon) => {
    setIconType(createIconData(icon));
  };

  // Handle cover upload
  const handleCoverChange = (coverUrl: string | null) => {
    if (coverUrl) {
      setIconType(createCoverData(coverUrl));
    } else {
      // If cover is removed, switch back to icon mode
      setIconType(createIconData('Folder'));
    }
  };

  // Handle toggle between icon and cover
  const handleToggleCoverMode = () => {
    const newMode = !useCustomCover;
    setUseCustomCover(newMode);

    // When switching to cover mode, clear any existing icon
    // When switching to icon mode, clear any existing cover
    if (newMode) {
      setIconType({ type: 'cover', cover_url: '' });
    } else {
      setIconType(createIconData('Folder'));
    }
  };

  // Get current cover URL if in cover mode
  const currentCoverUrl = iconType.type === 'cover' ? iconType.cover_url : undefined;
  const currentIcon = iconType.type === 'icon' ? iconType.icon : 'Folder';

  // Handle close with reset
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEditMode ? t('knowledge.dialog.editTitle') : t('knowledge.dialog.createTitle')}
      maxWidth='md'
      dataTestId='knowledge-base-dialog'
      footer={
        <div className='flex justify-end'>
          <button
            type='button'
            data-testid='kb-submit-button'
            onClick={handleSubmit}
            disabled={loading}
            className={clsx(
              'px-6 py-2 rounded-lg font-medium transition-colors',
              'bg-blue-600 text-white hover:bg-blue-700',
              'disabled:bg-gray-300 disabled:cursor-not-allowed'
            )}
          >
            {loading ? (
              <>
                <span data-testid='loading-spinner' className='inline-block animate-spin mr-2'>
                  ⏳
                </span>
                {isEditMode ? t('knowledge.dialog.updating') : t('knowledge.dialog.creating')}
              </>
            ) : isEditMode ? (
              t('knowledge.dialog.saveButton')
            ) : (
              t('knowledge.dialog.confirmButton')
            )}
          </button>
        </div>
      }
    >
      <form onSubmit={e => e.preventDefault()} className='space-y-6'>
        {/* Name Input - Required */}
        <div>
          <label htmlFor='kb-name' className='block text-sm font-medium text-gray-700 mb-2'>
            {t('knowledge.dialog.nameLabel')} <span className='text-red-500'>*</span>
          </label>
          <input
            id='kb-name'
            data-testid='kb-name-input'
            type='text'
            value={name}
            onChange={e => handleNameChange(e.target.value)}
            placeholder={t('knowledge.dialog.namePlaceholder')}
            disabled={loading}
            maxLength={100}
            required
            className={clsx(
              'w-full px-4 py-2 border rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'disabled:bg-gray-100 disabled:cursor-not-allowed',
              {
                'border-gray-300': !error,
                'border-red-500': error,
              }
            )}
          />
          {error && (
            <p data-testid='kb-name-error' className='mt-2 text-sm text-red-600' role='alert'>
              {error}
            </p>
          )}
        </div>

        {/* Cover (Cover Image/Icon Selector) - Moved to second position */}
        <div>
          <div className='flex items-center justify-between mb-3'>
            <h3 className='text-sm font-semibold text-gray-700'>
              {t('knowledge.dialog.coverLabel')}
            </h3>
            <button
              type='button'
              data-testid='kb-cover-mode-toggle'
              onClick={handleToggleCoverMode}
              disabled={loading}
              className={clsx(
                'text-xs px-3 py-1 rounded-md font-medium transition-colors',
                'bg-gray-100 text-gray-700 hover:bg-gray-200',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {useCustomCover
                ? t('knowledge.dialog.useIconButton')
                : t('knowledge.dialog.useCoverButton')}
            </button>
          </div>

          {!useCustomCover ? (
            /* Icon Mode */
            <div className='flex flex-col items-center'>
              <IconSelector value={currentIcon} onChange={handleIconChange} disabled={loading} />
            </div>
          ) : (
            /* Cover Mode */
            <div className='flex flex-col items-center'>
              <LogoUploader
                value={currentCoverUrl}
                onChange={handleCoverChange}
                disabled={loading}
              />
              <p className='mt-3 text-xs text-orange-600 text-center max-w-[250px]'>
                {t('knowledge.dialog.coverRestrictions')}
              </p>
            </div>
          )}
        </div>

        {/* Description Input - Optional */}
        <div>
          <label htmlFor='kb-description' className='block text-sm font-medium text-gray-700 mb-2'>
            {t('knowledge.dialog.descriptionLabel')}
          </label>
          <textarea
            id='kb-description'
            data-testid='kb-description-input'
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('knowledge.dialog.descriptionPlaceholder')}
            disabled={loading}
            maxLength={500}
            rows={3}
            className={clsx(
              'w-full px-4 py-2 border rounded-lg resize-none',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'disabled:bg-gray-100 disabled:cursor-not-allowed',
              'border-gray-300'
            )}
          />
          <p className='mt-1 text-xs text-gray-500'>{t('knowledge.dialog.descriptionHint')}</p>
        </div>
      </form>
    </Modal>
  );
};

export default CreateKnowledgeBaseDialog;
